import { useTranslation } from "react-i18next";
import { cn, Skeleton } from "@nexus/ui";
import type { AdminTab } from "./AdminTabStrip";

/**
 * What each admin tab looks like before its loader resolves.
 *
 * The route awaits its data, so this — not the per-panel `isLoading` branches —
 * is what an admin actually sees on first paint and on every tab switch. Each
 * shape mirrors the resolved layout it stands in for, so the panel does not
 * rearrange itself the moment the numbers land.
 */

/** Matches the widths the KPI strip reserves for its own stats. */
const KPI_WIDTHS = ["w-16", "w-20", "w-28", "w-28"];
/** The plot area of the events and token-usage charts. */
const CHART_HEIGHT_PX = 132;
/** The scheduled-jobs chart carries a job axis, so it runs taller. */
const JOBS_CHART_HEIGHT_PX = 148;
const TABLE_ROWS = 3;
const SETTINGS_RAIL_ITEMS = 5;

/**
 * Skeleton's default `bg-muted` is the same value as the card and header
 * surfaces these land on, so anything outside a table needs the ink wash to
 * show up at all.
 */
const WASH = "bg-foreground/8";

function Rows({ count, className }: { count: number; className?: string }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className={cn("h-10 w-full", className)} />
      ))}
    </>
  );
}

/** The bordered card the four list tabs put their table in. */
function TableCardSkeleton({ rows = TABLE_ROWS }: { rows?: number }) {
  return (
    <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-card">
      <div className="space-y-2 p-4">
        <Rows count={rows} />
      </div>
    </div>
  );
}

function ChartCardSkeleton({ height = CHART_HEIGHT_PX }: { height?: number }) {
  return (
    <section className="shrink-0 rounded-md border border-border bg-card">
      <div className="flex items-center gap-4 px-3 py-2">
        <Skeleton className={cn("h-4 w-20 rounded-sm", WASH)} />
        <Skeleton className={cn("ml-auto h-3 w-44 rounded-sm", WASH)} />
      </div>
      <div className="px-1 pb-2 pt-1">
        <Skeleton className="w-full rounded-sm" style={{ height }} />
      </div>
    </section>
  );
}

/** The time-range control stays a placeholder here — the window is not known yet. */
function KpiStripSkeleton() {
  return (
    <div className="-mx-1.5 flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3">
      <Skeleton className={cn("ml-1.5 h-8 w-44 rounded-sm", WASH)} />
      {KPI_WIDTHS.map((width, index) => (
        <Skeleton
          key={width}
          className={cn(
            "h-11 rounded-sm",
            WASH,
            width,
            index === KPI_WIDTHS.length - 1 && "ml-auto",
          )}
        />
      ))}
    </div>
  );
}

function EventsSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <KpiStripSkeleton />
      <ChartCardSkeleton />

      {/* Search, three filter selects, the pings toggle, then the count and
          refresh pinned right — the filter bar's own order. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Skeleton className={cn("h-8 w-56 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-32 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-32 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-40 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-28 rounded-sm", WASH)} />
        <Skeleton className={cn("ml-auto h-8 w-24 rounded-sm", WASH)} />
      </div>

      <TableCardSkeleton />
    </div>
  );
}

function DeploysSkeleton() {
  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Skeleton className={cn("h-8 w-[170px] rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-36 rounded-sm", WASH)} />
        <span className="flex-1" />
        <Skeleton className={cn("h-8 w-24 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-40 rounded-sm", WASH)} />
      </div>

      <ChartCardSkeleton />
      <TableCardSkeleton />
    </>
  );
}

function RoundupsSkeleton() {
  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Skeleton className={cn("h-4 w-[42ch] max-w-full rounded-sm", WASH)} />
        <span className="flex-1" />
        <Skeleton className={cn("h-8 w-28 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-40 rounded-sm", WASH)} />
      </div>

      {/* Two rows: roundups are composed by hand, so the list is short. */}
      <TableCardSkeleton rows={2} />
    </>
  );
}

function ScheduledJobsSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <Skeleton className={cn("h-4 w-[52ch] max-w-full rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-24 shrink-0 rounded-sm", WASH)} />
      </div>

      <ChartCardSkeleton height={JOBS_CHART_HEIGHT_PX} />
      <TableCardSkeleton />
    </div>
  );
}

/** One of the token-usage breakdown cards: a header row over its own rows. */
function UsageTableSkeleton() {
  return (
    <div className="shrink-0 rounded-md border border-border bg-card">
      <div className="flex items-center gap-4 px-3 py-2">
        <Skeleton className={cn("h-4 w-32 rounded-sm", WASH)} />
      </div>
      <div className="space-y-2 border-t border-border p-3">
        {Array.from({ length: TABLE_ROWS }, (_, index) => (
          <Skeleton key={index} className={cn("h-4 w-full rounded-sm", WASH)} />
        ))}
      </div>
    </div>
  );
}

function TokenUsageSkeleton() {
  return (
    <div className="flex shrink-0 flex-col gap-4 pb-6">
      <KpiStripSkeleton />

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Skeleton className={cn("h-8 w-[160px] rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-[240px] rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-[240px] rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-44 rounded-sm", WASH)} />
      </div>

      <ChartCardSkeleton />
      <UsageTableSkeleton />

      <div className="grid shrink-0 items-start gap-4 xl:grid-cols-2">
        <UsageTableSkeleton />
        <UsageTableSkeleton />
      </div>
    </div>
  );
}

/**
 * Both halves of the settings tab are the same shape — a group rail beside one
 * card panel, over the shared save footer — so they share a skeleton.
 */
function SettingsSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The section switcher above the rail. */}
      <Skeleton className={cn("h-8 w-64 shrink-0 rounded-sm", WASH)} />

      <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row md:gap-6">
        <div className="flex shrink-0 flex-row flex-wrap gap-1 md:w-56 md:flex-col">
          {Array.from({ length: SETTINGS_RAIL_ITEMS }, (_, index) => (
            <Skeleton key={index} className={cn("h-9 w-32 rounded-md md:w-full", WASH)} />
          ))}
        </div>

        <div className="min-h-0 flex-1 space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 border-t border-border pt-3">
        <Skeleton className={cn("h-4 w-56 rounded-sm", WASH)} />
        <Skeleton className={cn("ml-auto h-8 w-20 rounded-sm", WASH)} />
        <Skeleton className={cn("h-8 w-28 rounded-sm", WASH)} />
      </div>
    </div>
  );
}

const TAB_SKELETONS: Record<AdminTab, () => React.JSX.Element> = {
  events: EventsSkeleton,
  deploys: DeploysSkeleton,
  roundups: RoundupsSkeleton,
  scheduledJobs: ScheduledJobsSkeleton,
  tokenUsage: TokenUsageSkeleton,
  // The BigQuery tab resolves to the same arrangement token usage does — a
  // window strip, a filter row, a chart, then breakdown cards — so the two
  // share one stand-in rather than duplicating it.
  bigQuery: TokenUsageSkeleton,
  settings: SettingsSkeleton,
};

export function AdminTabSkeleton({ tab }: { tab: AdminTab }) {
  const { t } = useTranslation();
  const Shape = TAB_SKELETONS[tab];

  return (
    <div
      role="status"
      aria-busy
      aria-label={t("admin.loading", "Loading the admin panel…")}
      className="flex min-h-0 flex-1 flex-col gap-4"
    >
      <Shape />
    </div>
  );
}
